import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useCartStore } from '@/stores';
import { useSettingsStore } from '@/stores/settings.store';
import { formatPrice, toNumber } from '@/utils/format';
import { paymentService, type PaymentInitiateResponse } from '@/services';
import { ApiError } from '@/services/api';
import type { CartItem } from '@/types';
import { Button, CartItemCard, CartSummary } from '@/components/ui';

const WOMPI_WIDGET_SCRIPT_ID = 'wompi-widget-script';
const WOMPI_WIDGET_SCRIPT_URL = 'https://checkout.wompi.co/widget.js';

interface WompiWidgetResult {
  transaction?: {
    id?: string;
    status?: string;
  };
}

interface WompiWidgetInstance {
  open(callback: (result: WompiWidgetResult) => void): void;
}

type WompiWidgetConstructor = new (config: Record<string, unknown>) => WompiWidgetInstance;

declare global {
  interface Window {
    WidgetCheckout?: WompiWidgetConstructor;
  }
}

function loadWompiWidgetScript(): Promise<void> {
  if (window.WidgetCheckout) return Promise.resolve();

  const existing = document.getElementById(WOMPI_WIDGET_SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Wompi widget script failed to load')), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = WOMPI_WIDGET_SCRIPT_ID;
    script.src = WOMPI_WIDGET_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Wompi widget script failed to load'));
    document.head.appendChild(script);
  });
}

export function CartPage(): JSX.Element {
  const { t } = useTranslation('cart');
  const { t: tc } = useTranslation('catalog');
  const { t: commonT } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { cart, isLoading, error, fetchCart, updateItem, removeItem, clearCart } = useCartStore();
  const currency = useSettingsStore((s) => s.currency);
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());
  const [checkoutStep, setCheckoutStep] = useState<PaymentInitiateResponse | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [invalidItemIds, setInvalidItemIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const handleQuantityChange = async (item: CartItem, newQuantity: number) => {
    if (newQuantity < 1) return;
    
    setUpdatingItems(prev => new Set(prev).add(item.id));
    try {
      await updateItem(item.id, newQuantity);
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    setUpdatingItems(prev => new Set(prev).add(itemId));
    try {
      await removeItem(itemId);
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const handleClearCart = async () => {
    if (!window.confirm(t('confirm.clear'))) {
      return;
    }
    await clearCart();
    setCheckoutStep(null);
    setCheckoutError(null);
    setInvalidItemIds(new Set());
  };

  const handleProceedToCheckout = async () => {
    setCheckoutError(null);
    setInvalidItemIds(new Set());
    setCheckoutLoading(true);
    try {
      const result = await paymentService.initiate(currency);
      setCheckoutStep(result);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409 && err.data && typeof err.data === 'object' && 'invalidItems' in err.data) {
          const items = (err.data as { invalidItems: { cartItemId: string }[] }).invalidItems;
          setInvalidItemIds(new Set(items.map((i) => i.cartItemId)));
          setCheckoutError(commonT('common.checkoutItemsInvalid'));
        } else {
          setCheckoutError(err.message || commonT('common.error'));
        }
      } else {
        setCheckoutError(commonT('common.error'));
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handlePayWompi = async () => {
    const checkout = checkoutStep?.wompiCheckout;
    if (!checkout) {
      setCheckoutError(commonT('common.wompiCheckoutMissing'));
      return;
    }

    setCheckoutError(null);
    setCheckoutLoading(true);

    try {
      await loadWompiWidgetScript();
      if (!window.WidgetCheckout) {
        throw new Error('Wompi widget is unavailable');
      }

      const widget = new window.WidgetCheckout({
        currency: checkout.currency,
        amountInCents: checkout.amountInCents,
        reference: checkout.reference,
        publicKey: checkout.publicKey,
        signature: { integrity: checkout.signature },
        expirationTime: checkout.expirationTime,
      });

      widget.open(async (result) => {
        const transactionId = result.transaction?.id;
        if (!transactionId) {
          setCheckoutError(commonT('common.wompiMissingTransaction'));
          return;
        }

        setCheckoutLoading(true);
        try {
          const confirmResult = await paymentService.confirmWompi(transactionId);
          if (confirmResult.orderId) {
            setCheckoutStep(null);
            await fetchCart();
            await queryClient.invalidateQueries({ queryKey: ['orders'] });
            navigate(`/order-confirmation/${confirmResult.orderId}`);
            return;
          }

          setCheckoutError(confirmResult.statusMessage || commonT('common.wompiPaymentPending'));
        } catch (err) {
          if (err instanceof ApiError) {
            setCheckoutError(
              err.errorCode === 'PAYMENT_REJECTED' ? commonT('common.paymentRejected') : err.message || commonT('common.error'),
            );
          } else {
            setCheckoutError(commonT('common.error'));
          }
        } finally {
          setCheckoutLoading(false);
        }
      });
    } catch {
      setCheckoutError(commonT('common.wompiCheckoutMissing'));
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handlePaySimulation = async () => {
    if (!checkoutStep) return;
    setCheckoutError(null);
    setCheckoutLoading(true);
    try {
      const { orderId } = await paymentService.confirm(checkoutStep.sessionId);
      setCheckoutStep(null);
      await fetchCart();
      await queryClient.invalidateQueries({ queryKey: ['orders'] });
      navigate(`/order-confirmation/${orderId}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setCheckoutError(
          err.errorCode === 'PAYMENT_REJECTED' ? commonT('common.paymentRejected') : err.message || commonT('common.error'),
        );
      } else {
        setCheckoutError(commonT('common.error'));
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handlePayPendingSimulation = async () => {
    if (!checkoutStep) return;
    setCheckoutError(null);
    setCheckoutLoading(true);
    try {
      const { orderId } = await paymentService.confirm(checkoutStep.sessionId, { forcePending: true });
      setCheckoutStep(null);
      await fetchCart();
      await queryClient.invalidateQueries({ queryKey: ['orders'] });
      navigate(`/order-confirmation/${orderId}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setCheckoutError(err.message || commonT('common.error'));
      } else {
        setCheckoutError(commonT('common.error'));
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  /** Misma rama que `forceFailure` en POST /payment/confirm: no se crea orden pagada; respuesta 400 PAYMENT_REJECTED. */
  const handleSimulatePaymentRejected = async () => {
    if (!checkoutStep) return;
    setCheckoutError(null);
    setCheckoutLoading(true);
    try {
      await paymentService.confirm(checkoutStep.sessionId, { forceFailure: true });
    } catch (err) {
      if (err instanceof ApiError && err.errorCode === 'PAYMENT_REJECTED') {
        setCheckoutError(commonT('common.paymentRejected'));
      } else if (err instanceof ApiError) {
        setCheckoutError(err.message || commonT('common.error'));
      } else {
        setCheckoutError(commonT('common.error'));
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (isLoading && !cart) {
    return (
      <div className="flex min-h-[400px] items-center justify-center px-4">
        <div className="font-sans text-lg text-brand-700">{t('loading')}</div>
      </div>
    );
  }

  if (error && !cart) {
    return (
      <div className="flex min-h-[400px] items-center justify-center px-4">
        <div className="font-sans text-lg text-red-600">{error || t('error.generic')}</div>
      </div>
    );
  }

  if (!cart || cart.cartItems.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
        <h1 className="mb-6 font-heading text-heading-2xl text-brand-950">{t('title')}</h1>
        <div className="rounded-lg border border-brand-700/20 bg-surface p-8 text-center">
          <p className="mb-4 font-sans text-lg text-brand-700">{t('empty.text')}</p>
          <Button type="button" onClick={() => navigate('/products')}>
            {t('empty.goBrowse')}
          </Button>
        </div>
      </div>
    );
  }

  const totalItems = cart.cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const hasInvalidItems = cart.cartItems.some(item => !item.isValid);

  // Calculate totals grouped by currency
  const totalsByCurrency = cart.cartItems.reduce((acc, item) => {
    if (!item.isValid || !item.priceSnapshot) {
      return acc;
    }
    const currency = item.priceSnapshot.currency;
    const priceNum = toNumber(item.priceSnapshot.price);
    const itemTotal = priceNum * item.quantity;
    acc[currency] = (acc[currency] || 0) + itemTotal;
    return acc;
  }, {} as Record<string, number>);

  const currencies = Object.keys(totalsByCurrency);
  const hasMultipleCurrencies = currencies.length > 1;
  const isWompiCheckout = checkoutStep?.paymentMode === 'wompi';

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <h1 className="font-heading text-heading-2xl text-brand-950">{t('title')}</h1>
        {cart.cartItems.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-red-600 text-red-600 hover:bg-red-50"
            onClick={handleClearCart}
          >
            {t('action.clear')}
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-100 p-3 text-red-800">{error}</div>
      )}

      {checkoutError && (
        <div className="mb-4 rounded-md bg-red-100 p-3 text-red-800">{checkoutError}</div>
      )}

      {hasInvalidItems && (
        <div className="mb-4 rounded-md bg-amber-100 p-3 text-amber-900">{t('warn.invalidItems')}</div>
      )}

      {invalidItemIds.size > 0 && (
        <div className="mb-4 rounded-md bg-amber-100 p-3 text-amber-900">
          {commonT('common.checkoutItemsInvalid')}
        </div>
      )}

      {checkoutStep ? (
        <div className="mb-4 rounded-lg border border-brand-400/40 bg-brand-100 p-4">
          <h3 className="mb-2 font-heading text-heading-md text-brand-950">{commonT('common.chargeInCop')}</h3>
          <p className="mb-1 font-heading text-heading-xl text-brand-950">
            {formatPrice(checkoutStep.totalInCop, 'COP')}
          </p>
          <p className="mb-3 font-sans text-sm text-brand-700">
            {commonT('common.totalInYourCurrency')}:{' '}
            {formatPrice(checkoutStep.totalInDisplayCurrency, checkoutStep.displayCurrency)}
          </p>
          <p className="mb-3 font-sans text-xs text-brand-700">
            {isWompiCheckout ? commonT('common.wompiSandboxNote') : commonT('common.checkoutSimulationNote')}
          </p>
          <div className="flex flex-wrap gap-2">
            {isWompiCheckout ? (
              <Button
                type="button"
                onClick={handlePayWompi}
                disabled={checkoutLoading || !checkoutStep.wompiCheckout}
                loading={checkoutLoading}
              >
                {commonT('common.payWithWompiSandbox')}
              </Button>
            ) : (
              <>
                <Button type="button" onClick={handlePaySimulation} disabled={checkoutLoading} loading={checkoutLoading}>
                  {commonT('common.paySimulation')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handlePayPendingSimulation}
                  disabled={checkoutLoading}
                  loading={checkoutLoading}
                >
                  {commonT('common.paySimulationPending')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="border-red-600 text-red-600 hover:bg-red-50"
                  onClick={handleSimulatePaymentRejected}
                  disabled={checkoutLoading}
                  loading={checkoutLoading}
                >
                  {commonT('common.paySimulationRejected')}
                </Button>
              </>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCheckoutStep(null);
                setCheckoutError(null);
              }}
              disabled={checkoutLoading}
            >
              {commonT('common.cancel')}
            </Button>
          </div>
        </div>
      ) : null}

      {hasMultipleCurrencies && (
        <div className="mb-4 rounded-md bg-brand-200/50 p-3 text-brand-950">{t('warn.multipleCurrencies')}</div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {cart.cartItems.map((item) => {
            const isUpdating = updatingItems.has(item.id);
            const isProduct = !!item.product;
            const isTcg = !!item.tcgListing;

            let imageUrl = 'https://via.placeholder.com/150x150?text=No+Image';
            let name = t('misc.unknownItem');
            let priceDisplay = t('misc.na');
            let productHref: string | undefined;

            if (isProduct && item.product) {
              name = item.product.name;
              productHref = `/products/${item.product.id}`;
              imageUrl =
                item.product.images && item.product.images.length > 0
                  ? item.product.images[0].url
                  : imageUrl;
              priceDisplay = item.priceSnapshot
                ? formatPrice(item.priceSnapshot.price, item.priceSnapshot.currency)
                : formatPrice(item.product.price, item.product.currency);
            } else if (isTcg && item.tcgListing) {
              name =
                item.tcgListing.name ||
                item.tcgListing.masterCard?.name ||
                t('misc.unknownItem');
              imageUrl =
                item.tcgListing.product?.images && item.tcgListing.product.images.length > 0
                  ? item.tcgListing.product.images[0].url
                  : item.tcgListing.masterCard?.imageUrl || imageUrl;
              priceDisplay = item.priceSnapshot
                ? formatPrice(item.priceSnapshot.price, item.priceSnapshot.currency)
                : formatPrice(item.tcgListing.price, item.tcgListing.currency);
            }

            const lineTotal =
              item.priceSnapshot != null
                ? t('line.itemTotal', {
                    currency: item.priceSnapshot.currency,
                    amount: (toNumber(item.priceSnapshot.price) * item.quantity).toFixed(2),
                  })
                : undefined;

            const meta =
              isTcg && item.tcgListing ? (
                <>
                  <div>
                    <span className="font-semibold">{t('labels.seller')}</span>{' '}
                    {item.tcgListing.seller?.displayName || t('misc.unknown')}
                  </div>
                  <div>
                    <span className="font-semibold">{t('labels.condition')}</span>{' '}
                    {item.tcgListing.condition}
                  </div>
                  {item.tcgListing.language ? (
                    <div>
                      <span className="font-semibold">{t('labels.language')}</span>{' '}
                      {item.tcgListing.language}
                    </div>
                  ) : null}
                  {item.tcgListing.printingVariant ? (
                    <div>
                      <span className="font-semibold">{t('labels.variant')}</span>{' '}
                      {item.tcgListing.printingVariant}
                    </div>
                  ) : null}
                </>
              ) : null;

            return (
              <CartItemCard
                key={item.id}
                imageUrl={imageUrl}
                name={name}
                productHref={productHref}
                unitPrice={priceDisplay}
                lineTotal={lineTotal}
                quantity={item.quantity}
                onQuantityChange={(next) => handleQuantityChange(item, next)}
                onRemove={() => handleRemoveItem(item.id)}
                disabled={isUpdating}
                isValid={item.isValid}
                validationMessage={item.validationError || t('item.unavailable')}
                meta={meta}
                sellerBadge={
                  isTcg && item.tcgListing
                    ? {
                        label:
                          item.tcgListing.sellerLabel === 'ADMIN'
                            ? tc('seller.admin')
                            : tc('seller.seller'),
                        variant: item.tcgListing.sellerLabel === 'ADMIN' ? 'brand' : 'neutral',
                      }
                    : undefined
                }
                removeLabel={commonT('aria.removeFromCart')}
                decreaseLabel={commonT('aria.decreaseQuantity')}
                increaseLabel={commonT('aria.increaseQuantity')}
              />
            );
          })}
        </div>

        <div className="lg:col-span-1">
          <CartSummary
            title={t('summary.title')}
            itemsLabel={t('summary.items')}
            itemCount={totalItems}
            totals={
              currencies.length === 0
                ? [{ label: `${t('summary.total')}:`, amount: t('misc.na') }]
                : currencies.map((currency) => ({
                    label: `${t('summary.total')}${hasMultipleCurrencies ? ` (${currency})` : ':'}`,
                    amount: formatPrice(totalsByCurrency[currency], currency),
                  }))
            }
            checkoutLabel={checkoutLoading ? commonT('common.loading') : t('action.proceedCheckout')}
            onCheckout={handleProceedToCheckout}
            checkoutDisabled={hasInvalidItems || checkoutLoading}
            checkoutLoading={checkoutLoading}
          />
        </div>
      </div>
    </div>
  );
}
